import {
  ButtonHTMLAttributes,
  forwardRef,
  ForwardRefRenderFunction,
} from 'react';
import styled, { DefaultTheme } from 'styled-components';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { getTheme } from '../../theme/getTheme';
import { mediaScreen } from '../../utils/mediaScreen';

const ButtonFC: ForwardRefRenderFunction<
  any,
  ButtonHTMLAttributes<any> & IButton
> = (
  {
    mType = 'primary',
    mSize = 'large',
    mColor = 'teal',
    disabled,
    children,
    loading,
    invert,
    className,
    ...props
  },
  ref
) => {
  const theme = getTheme();
  const loaderColor =
    mType === 'secondary'
      ? invert
        ? theme.colors.slate1
        : theme.colors.white
      : 'white';

  return (
    <StyledButton
      className={`button mButton mButton-${mType} mButton-${mSize} ${
        className || ''
      }`}
      mType={mType}
      mColor={mColor}
      mSize={mSize}
      disabled={disabled || loading}
      invert={invert}
      as={(props as any)?.href ? 'a' : 'button'}
      {...props}
      ref={ref}
    >
      {!loading ? (
        children
      ) : (
        <Loader top={mSize === 'large' ? -8 : -9} className="loader">
          <PropagateLoader size={10} color={loaderColor} />
        </Loader>
      )}
    </StyledButton>
  );
};

export const Button = forwardRef(ButtonFC);

export interface IButton {
  mType?: 'primary' | 'secondary';
  mSize?: 'large' | 'medium';
  disabled?: boolean;
  mColor?: keyof DefaultTheme['colors'];
  loading?: boolean;
  invert?: boolean;
  as?: any;
}

const StyledButton = styled.button<IButton>`
  appearance: none;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.slate1};
  cursor: pointer;
  outline: none !important;
  background-color: ${({ theme, mColor }) => mColor && theme.colors[mColor]};
  border: 1px solid ${({ theme, mColor }) => mColor && theme.colors[mColor]};
  user-select: none;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:empty {
    display: none;
  }

  ${({ disabled }) =>
    disabled &&
    `
    pointer-events: none;
    opacity: 0.4;  
  `}

  ${({ mSize }) => {
    switch (mSize) {
      case 'medium':
        return `
          padding: 0 24px;
          font-size: 16px;
          height: 48px;
        `;

      case 'large':
        return `
          padding: 0 32px;
          font-size: 18px;
          height: 60px;
        `;

      default:
        return '';
    }
  }}

  ${mediaScreen('xs', 'sm')} {
    /* padding: 10px 16px;
    font-size: 15px;
    height: 46px; */
  }

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.8;
  }

  ${({ theme, mType, invert }) =>
    mType === 'secondary' &&
    `
      background-color: transparent;
      color: ${invert ? theme.colors.slate1 : theme.colors.white};
      border-color: ${invert ? theme.colors.slate1 : theme.colors.white};
    `}

  ${({ theme, mType, invert }) =>
    mType === 'primary' &&
    invert &&
    `
      background-color: ${theme.colors.slate1};
      border-color: ${theme.colors.slate1};
      color: ${theme.colors.white};
    `}
`;

const Loader = styled.div<{ top?: number }>`
  width: 100%;
  height: 100%;

  > span {
    display: inline-block;
    top: ${(props) => props.top}px;
  }
`;
